import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { publicInstance as axiosInstance } from '../axiosConfig';
import { Project, ProjectSettings } from './projectSlice';
import Cookies from 'js-cookie';
import _ from 'lodash';

// Interface for author information
export interface Author {
    name: string;
    bio: string | null;
    profile_image: string | null;
    slug: string;
}

// Interface for a single blog post
export interface Post {
    slug: string;
    id: string;
    uuid: string;
    title: string;
    html: string;
    comment_id: string;
    feature_image: string | null;
    feature_image_alt: string | null;
    feature_image_caption: string | null;
    featured: boolean;
    visibility: string;
    created_at: string;
    updated_at: string;
    published_at: string;
    custom_excerpt: string | null;
    codeinjection_head: string | null;
    codeinjection_foot: string | null;
    custom_template: string | null;
    canonical_url: string | null;
    url: string;
    excerpt: string;
    reading_time: number;
    access: boolean;
    og_image: string | null;
    og_title: string | null;
    og_description: string | null;
    twitter_image: string | null;
    twitter_title: string | null;
    twitter_description: string | null;
    meta_title: string | null;
    meta_description: string | null;
    email_subject: string | null;
    tags: string[];
    primary_author: Author;
    authors: Author[];
}

// Interface for pagination metadata
interface PaginationMeta {
    page: number;
    limit: number;
    pages: number;
    total: number;
    next: number | null;
    prev: number | null;
}

// Interface for API response
interface PostsResponse {
    posts: Post[];
    meta: {
        pagination: PaginationMeta;
    };
}

// Interface for get posts parameters
interface GetPostsParams {
    page?: number;
    limit?: number;
    include?: string;
    [key: string]: any; // For any additional query parameters
}

// Interface for our slice state
interface BlogState {
    posts: Post[];
    pagination: PaginationMeta | null;
    loading: boolean;
    error: string | null;
    currentPost: Post | null;
    currentPostLoading: boolean;
    currentPostError: string | null;
}

// Initial state
const initialState: BlogState = {
    posts: [],
    pagination: null,
    loading: false,
    error: null,
    currentPost: null,
    currentPostLoading: false,
    currentPostError: null,
};

// Async thunk for fetching posts
export const fetchPosts = createAsyncThunk<PostsResponse, GetPostsParams>(
    'blog/fetchPosts',
    async (params: GetPostsParams, { rejectWithValue }) => {
        try {
            // Ensure include parameter has default value if not provided
            const queryParams = {
                include: 'tags,authors',
                ...params
            };

            const response = await axiosInstance.get('/blog/posts', {
                params: queryParams,
                headers: {
                    'Accept-Version': 'v5.0' // Adjust version as needed
                }
            });

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error || 'Failed to fetch posts');
        }
    }
);
// /blog/posts/slug/{slug}
// Async thunk for fetching a single post
// Async thunk for fetching posts
export const fetchPostBySlug = createAsyncThunk<Post, string>(
    'blog/fetchPostBySlug',
    async (slug: string, { rejectWithValue }) => {
        try {
            // Ensure include parameter has default value if not provided
            const queryParams = {
                include: 'tags,authors',
            };

            const response = await axiosInstance.get(`/blog/posts/slug/${slug}`, {
                params: queryParams,
                headers: {
                    'Accept-Version': 'v5.0' // Adjust version as needed
                }
            });

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error || 'Failed to fetch posts');
        }
    }
);

// Create the slice
const blogSlice = createSlice({
    name: 'blog',
    initialState,
    reducers: {
        clearPosts: (state) => {
            state.posts = [];
            state.pagination = null;
        },
        clearCurrentPost: (state) => {
            state.currentPost = null;
            state.currentPostError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPosts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPosts.fulfilled, (state, action) => {
                state.loading = false;
                state.posts = action.payload.posts;
                state.pagination = action.payload.meta.pagination;
            })
            .addCase(fetchPosts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(fetchPostBySlug.pending, (state) => {
                state.currentPostLoading = true;
                state.currentPostError = null;
            })
            .addCase(fetchPostBySlug.fulfilled, (state, action) => {
                console.log('action payload', action);
                state.currentPostLoading = false;
                state.currentPost = action.payload;
            })
            .addCase(fetchPostBySlug.rejected, (state, action) => {
                state.currentPostLoading = false;
                state.currentPostError = action.payload as string;
            });
    }
});

export const { clearPosts, clearCurrentPost } = blogSlice.actions;
export default blogSlice.reducer;


